import { useState, useEffect } from 'react';
import { ChevronRight, Building2, User, Mail, Phone, MapPin } from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import useAuthStore from '../../../stores/authStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const SITE_TYPES = [
  { value: 'k12_school', label: 'School (K-12)' },
  { value: 'commercial', label: 'Commercial / Office' },
  { value: 'residential', label: 'Residential Community' },
  { value: 'house_of_worship', label: 'House of Worship' },
  { value: 'government', label: 'Government' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'warehouse', label: 'Warehouse / Industrial' },
  { value: 'other', label: 'Other' },
];

const ProfileStep = ({ onNext }) => {
  const { savedData, markStepComplete, tenant } = useSiteSetupStore();
  const { user } = useAuthStore();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    orgName: '',
    siteName: '',
    siteType: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Pre-populate from user data and saved wizard data
  useEffect(() => {
    const saved = savedData?.profile || {};
    const nameParts = (user?.name || '').split(' ');

    setForm({
      firstName: saved.firstName || nameParts[0] || '',
      lastName: saved.lastName || nameParts.slice(1).join(' ') || '',
      orgName: saved.orgName || tenant?.organizationName || '',
      siteName: saved.siteName || tenant?.name || '',
      siteType: saved.siteType || '',
      contactEmail: saved.contactEmail || user?.email || '',
      contactPhone: saved.contactPhone || '',
      address: saved.address || '',
      city: saved.city || '',
      state: saved.state || '',
      zip: saved.zip || '',
    });
  }, [user, tenant, savedData]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.orgName.trim()) errs.orgName = 'Required';
    if (!form.contactEmail.trim()) errs.contactEmail = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) errs.contactEmail = 'Invalid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      // Save profile data to backend (updates User + Organization + Tenant)
      const res = await fetchWithTenant('/api/onboarding/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'profile',
          data: { profile: form }
        })
      });

      if (res.ok) {
        // Also update user name if changed
        if (form.firstName || form.lastName) {
          try {
            await fetchWithTenant('/api/user/settings', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: (form.firstName + ' ' + form.lastName).trim()
              })
            });
          } catch (e) {
            // Non-critical, continue
          }
        }

        await markStepComplete('profile', { profile: form });
        onNext();
      }
    } catch (err) {
      console.error('[ProfileStep] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="site-setup-step profile-step">
      <h3>Your Information</h3>
      <p className="step-description">Tell us about yourself and your site. This helps us customize your experience.</p>

      <div className="setup-form-section">
        <h4><User size={16} /> Contact</h4>
        <div className="setup-form-row">
          <div className="setup-form-field">
            <label>First Name *</label>
            <input
              type="text"
              value={form.firstName}
              onChange={e => updateField('firstName', e.target.value)}
              className={errors.firstName ? 'error' : ''}
              placeholder="First name"
            />
            {errors.firstName && <span className="field-error">{errors.firstName}</span>}
          </div>
          <div className="setup-form-field">
            <label>Last Name *</label>
            <input
              type="text"
              value={form.lastName}
              onChange={e => updateField('lastName', e.target.value)}
              className={errors.lastName ? 'error' : ''}
              placeholder="Last name"
            />
            {errors.lastName && <span className="field-error">{errors.lastName}</span>}
          </div>
        </div>
        <div className="setup-form-row">
          <div className="setup-form-field">
            <label><Mail size={14} /> Email *</label>
            <input
              type="email"
              value={form.contactEmail}
              onChange={e => updateField('contactEmail', e.target.value)}
              className={errors.contactEmail ? 'error' : ''}
              placeholder="you@company.com"
            />
            {errors.contactEmail && <span className="field-error">{errors.contactEmail}</span>}
          </div>
          <div className="setup-form-field">
            <label><Phone size={14} /> Phone</label>
            <input
              type="tel"
              value={form.contactPhone}
              onChange={e => updateField('contactPhone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>
      </div>

      <div className="setup-form-section">
        <h4><Building2 size={16} /> Organization & Site</h4>
        <div className="setup-form-row">
          <div className="setup-form-field">
            <label>Organization Name *</label>
            <input
              type="text"
              value={form.orgName}
              onChange={e => updateField('orgName', e.target.value)}
              className={errors.orgName ? 'error' : ''}
              placeholder="Your company or organization"
            />
            {errors.orgName && <span className="field-error">{errors.orgName}</span>}
          </div>
          <div className="setup-form-field">
            <label>Site Name</label>
            <input
              type="text"
              value={form.siteName}
              onChange={e => updateField('siteName', e.target.value)}
              placeholder="Main Office, Building A, etc."
            />
          </div>
        </div>
        <div className="setup-form-row">
          <div className="setup-form-field full-width">
            <label>Site Type</label>
            <select value={form.siteType} onChange={e => updateField('siteType', e.target.value)}>
              <option value="">-- Select site type --</option>
              {SITE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="setup-form-section">
        <h4><MapPin size={16} /> Address (Optional)</h4>
        <div className="setup-form-row">
          <div className="setup-form-field full-width">
            <input
              type="text"
              value={form.address}
              onChange={e => updateField('address', e.target.value)}
              placeholder="Street address"
            />
          </div>
        </div>
        <div className="setup-form-row three-col">
          <div className="setup-form-field">
            <input type="text" value={form.city} onChange={e => updateField('city', e.target.value)} placeholder="City" />
          </div>
          <div className="setup-form-field">
            <input type="text" value={form.state} onChange={e => updateField('state', e.target.value)} placeholder="State" />
          </div>
          <div className="setup-form-field">
            <input type="text" value={form.zip} onChange={e => updateField('zip', e.target.value)} placeholder="ZIP" />
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button className="site-setup-btn primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'Continue'} {!saving && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );
};

export default ProfileStep;
